/**
 * EditCenterPage - Super Admin Only
 * 
 * This page is exclusively for super_admin users to:
 * - Create new centers (/centers/new)
 * - Edit existing centers (/center/edit/:centerId)
 * 
 * Features available to super_admin:
 * - Full center management (name, address, contact info)
 * - Branding settings (subdomain, colors)
 * - License information management
 * - Admin account creation for new centers
 * - Center deletion capabilities
 * 
 * Note: Regular admins use AdminEditCenterPage for editing their own center
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import api from '../api/api';
import '../styles/EditCenterPage.css';

const EditCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [center, setCenter] = useState({
    name: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    whatsapp: '',
    facebook: '',
    logoUrl: null,
    subdomainPrefix: '',
    licenseLevel: '',
    licenseStartDate: '',
    licenseEndDate: '',
    adminName: '',
    brandingSettings: {
      primaryColor: '#4a90e2',
      secondaryColor: '#333333',
      accentColor: '#ff9800'
    }
  });
  const [originalSubdomain, setOriginalSubdomain] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sendActivationEmail, setSendActivationEmail] = useState(true); // Default to checked

  // Get centerId from URL params if available
  const { centerId } = useParams();
  
  
  useEffect(() => {
    // Check if user has permission to edit center
    if (!user || user.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }

    // Fetch center data - either from params or creating new
    if (centerId) {
      // Super admin editing a specific center
      fetchCenterData(centerId);
    } else {
      // Creating new center or no center to load
      setLoading(false);
    }
  }, [user, navigate, centerId]);

  const fetchCenterData = async (centerId) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/centers/${centerId}`);
      
      if (response.data) {
        const centerData = response.data.success ? response.data.data : response.data;
        
        setCenter({
          name: centerData.name || '',
          address: centerData.address || '',
          contactEmail: centerData.contactEmail || centerData.email || '',
          contactPhone: centerData.contactPhone || centerData.phone || '',
          website: centerData.website || '',
          whatsapp: centerData.whatsapp || '',
          facebook: centerData.facebook || '',
          logoUrl: centerData.logoUrl || null,
          subdomainPrefix: centerData.subdomainPrefix || '',
          licenseLevel: centerData.licenseLevel || '',
          licenseStartDate: centerData.licenseStartDate || '',
          licenseEndDate: centerData.licenseEndDate || '',
          brandingSettings: {
            primaryColor: centerData.brandingSettings?.primaryColor || '#4a90e2',
            secondaryColor: centerData.brandingSettings?.secondaryColor || '#333333',
            accentColor: centerData.brandingSettings?.accentColor || '#ff9800'
          }
        });
        
        // Store original subdomain for comparison during save
        setOriginalSubdomain(centerData.subdomainPrefix || '');
        
        // If center has a logo, set the preview
        if (centerData.logoUrl) {
          setLogoPreview(centerData.logoUrl);
        }
      }
    } catch (err) {
      console.error('Error fetching center data:', err);
      setError('Failed to load center data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      // Handle nested properties (for branding settings)
      const [parent, child] = name.split('.');
      setCenter(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setCenter(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!center.name || !center.contactEmail) {
      setError('Please fill in all required fields.');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(center.contactEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      // Log submission details for debugging
      console.log('Submitting center data:', {
        ...center,
        subdomainPrefix: center.subdomainPrefix,
        originalSubdomain,
        isSubdomainChanged: center.subdomainPrefix !== originalSubdomain,
        userRole: user.role
      });
      
      // Handle create vs update operations
      if (!centerId) {
        // Creating new center
        const centerData = {
          name: center.name,
          address: center.address || '',
          contactEmail: center.contactEmail,
          contactPhone: center.contactPhone || '',
          website: center.website || '',
          whatsapp: center.whatsapp || '',
          facebook: center.facebook || '',
          licenseLevel: center.licenseLevel || '',
          licenseStartDate: center.licenseStartDate || '',
          licenseEndDate: center.licenseEndDate || '',
          subdomainPrefix: center.subdomainPrefix || '',
          brandingSettings: center.brandingSettings,
          sendActivationEmail: sendActivationEmail,
          adminName: center.adminName || ''
        };
        
        console.log('Creating new center:', centerData);
        
        const response = await api.post('/centers', centerData);
        
        if (response.data) {
          console.log('Center created successfully:', response.data);
          
          // If there's a logo file and center was created successfully, upload it
          if (logoFile && response.data.data && response.data.data._id) {
            console.log('Uploading logo for new center...');
            const centerId = response.data.data._id;
            const logoFormData = new FormData();
            logoFormData.append('logo', logoFile);
            
            await api.put(`/centers/${centerId}`, logoFormData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            console.log('Logo uploaded successfully for new center');
          }
          
          setSuccess(true);
          
          setTimeout(() => {
            navigate('/centers');
          }, 1500);
        }
        return;
      }
      
      // Determine which center ID to use (from URL params or user context)
      const targetCenterId = centerId || user.center;
      if (!targetCenterId) {
        throw new Error('No center ID available for update');
      }
      
      // Super admin handling - separate requests for different types of updates
        // First, update basic center info and license data
        const centerUpdateData = {
          name: center.name,
          address: center.address || '',
          contactEmail: center.contactEmail,
          contactPhone: center.contactPhone || '',
          website: center.website || '',
          whatsapp: center.whatsapp || '',
          facebook: center.facebook || '',
          licenseLevel: center.licenseLevel || '',
          licenseStartDate: center.licenseStartDate || '',
          licenseEndDate: center.licenseEndDate || ''
        };
        
        console.log('Sending center update data:', centerUpdateData);
        
        // Send center update request
        await api.put(`/centers/${targetCenterId}`, centerUpdateData);
        
        // Then, update branding settings if any changed
        const brandingUpdateData = {
          subdomainPrefix: center.subdomainPrefix || '',
          brandingSettings: {
            primaryColor: center.brandingSettings?.primaryColor || '#4a90e2',
            secondaryColor: center.brandingSettings?.secondaryColor || '#333333',
            accentColor: center.brandingSettings?.accentColor || '#ff9800'
          }
        };
        
        console.log('Sending branding update data:', brandingUpdateData);
        
        // Send branding update request
        const response = await api.put(`/centers/${targetCenterId}/branding`, brandingUpdateData);
        
        // If there's a new logo file, upload it separately
        if (logoFile) {
          console.log('Uploading logo file...');
          const logoFormData = new FormData();
          logoFormData.append('logo', logoFile);
          
          await api.put(`/centers/${targetCenterId}`, logoFormData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          console.log('Logo uploaded successfully');
        }
        
        if (response.data) {
          console.log('Update successful:', response.data);
          setSuccess(true);
          
          // Navigate back to appropriate page after success
          setTimeout(() => {
            if (centerId) {
              navigate(`/center/details/${targetCenterId}`);
            } else {
              navigate('/dashboard');
            }
          }, 1500);
        }
    } catch (err) {
      console.error('Error updating center:', err);
      setError(`Failed to update center information: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!centerId) return;
    
    try {
      setSubmitting(true);
      setError('');
      
      await api.delete(`/centers/${centerId}`);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/centers');
      }, 1500);
    } catch (err) {
      console.error('Error deleting center:', err);
      setError(`Failed to delete center: ${err.message}`);
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="edit-center-container">
      <div className="header">
        <div className="header-left">
          <Link to={centerId ? "/centers" : "/settings"} className="back-link">
            &larr; Back to {centerId ? "Centers" : "Settings"}
          </Link>
          <h1>{centerId ? "Edit Center Information" : "Add New Center"}</h1>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading center data...</p>
        </div>
      ) : (
        <div className="edit-center-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{centerId ? 'Center information updated successfully!' : 'Center created successfully!'}</div>}
          
          <form onSubmit={handleSubmit} className="center-form">
            <div className="form-sections">
              <div className="form-section logo-section">
                <h2>Center Logo</h2>
                <div className="logo-container">
                  <div className="logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Center Logo" className="logo-image" />
                    ) : (
                      <div className="logo-placeholder">
                        <i className="placeholder-icon">📷</i>
                        <span>No Logo</span>
                      </div>
                    )}
                  </div>
                  <label className="logo-upload-button">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleLogoChange} 
                      className="file-input"
                    />
                  </label>
                </div>
              </div>

              <div className="form-section center-details">
                <h2>Center Details</h2>
                
                <div className="form-group">
                  <label htmlFor="name">Center Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={center.name}
                    onChange={handleInputChange}
                    placeholder="Enter center name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={center.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contactEmail">Contact Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={center.contactEmail}
                    onChange={handleInputChange}
                    placeholder="Enter contact email"
                    required
                  />
                </div>

                {/* Admin Name - Only show when creating new center */}
                {!centerId && sendActivationEmail && (
                  <div className="form-group">
                    <label htmlFor="adminName">Admin Name</label>
                    <input
                      type="text"
                      id="adminName"
                      name="adminName"
                      value={center.adminName || ''}
                      onChange={handleInputChange}
                      placeholder="Enter admin name"
                    />
                    <small className="help-text">
                      Name for the admin user account that will be created
                    </small>
                  </div>
                )}

                {/* Activation Email Checkbox - Only show when creating new center */}
                {!centerId && (
                  <div className="form-group">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="sendActivationEmail"
                        checked={sendActivationEmail}
                        onChange={(e) => setSendActivationEmail(e.target.checked)}
                      />
                      <label htmlFor="sendActivationEmail" className="checkbox-label">
                        Send activation email to admin
                      </label>
                    </div>
                    <small className="help-text">
                      Creates admin account and sends welcome email with setup instructions
                    </small>
                  </div>
                )}
                
                <div className="form-group">
                  <label htmlFor="contactPhone">Contact Phone</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={center.contactPhone}
                    onChange={handleInputChange}
                    placeholder="Enter contact phone"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={center.website}
                    onChange={handleInputChange}
                    placeholder="e.g. https://yourwebsite.org or www.yourwebsite.org"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="whatsapp">WhatsApp</label>
                  <input
                    type="text"
                    id="whatsapp"
                    name="whatsapp"
                    value={center.whatsapp}
                    onChange={handleInputChange}
                    placeholder="e.g. +447957006983"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="facebook">Facebook</label>
                  <input
                    type="url"
                    id="facebook"
                    name="facebook"
                    value={center.facebook}
                    onChange={handleInputChange}
                    placeholder="e.g. https://facebook.com/pagename"
                  />
                </div>
              </div>
              
              {/* Branding section */}
                <div className="form-section branding-section">
                  <h2>Branding Settings</h2>
                  
                  <div className="form-group">
                    <label htmlFor="subdomainPrefix">Subdomain Prefix</label>
                    <div className="subdomain-input-group">
                      <input
                        type="text"
                        id="subdomainPrefix"
                        name="subdomainPrefix"
                        value={center.subdomainPrefix}
                        onChange={handleInputChange}
                        placeholder="Enter subdomain prefix"
                        pattern="[a-z0-9-]+"
                      />
                      <span className="subdomain-suffix">.memoraize.io</span>
                    </div>
                    <small className="help-text">Only lowercase letters, numbers, and hyphens allowed</small>
                  </div>
                  
                  <div className="color-pickers">
                    <div className="form-group">
                      <label htmlFor="brandingSettings.primaryColor">Primary Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.primaryColor"
                          name="brandingSettings.primaryColor"
                          value={center.brandingSettings.primaryColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.primaryColor}
                          name="brandingSettings.primaryColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="brandingSettings.secondaryColor">Secondary Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.secondaryColor"
                          name="brandingSettings.secondaryColor"
                          value={center.brandingSettings.secondaryColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.secondaryColor}
                          name="brandingSettings.secondaryColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="brandingSettings.accentColor">Accent Color</label>
                      <div className="color-input-group">
                        <input
                          type="color"
                          id="brandingSettings.accentColor"
                          name="brandingSettings.accentColor"
                          value={center.brandingSettings.accentColor}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          value={center.brandingSettings.accentColor}
                          name="brandingSettings.accentColor"
                          onChange={handleInputChange}
                          className="color-text-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="branding-preview">
                    <h3>Preview</h3>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.primaryColor }}>Primary</div>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.secondaryColor, color: '#fff' }}>Secondary</div>
                    <div className="color-preview" style={{ backgroundColor: center.brandingSettings.accentColor }}>Accent</div>
                  </div>
                </div>
              
              {/* License Information section */}
                <div className="form-section license-section">
                  <h2>License Information</h2>
                  
                  <div className="form-group">
                    <label htmlFor="licenseLevel">License Level</label>
                    <select
                      id="licenseLevel"
                      name="licenseLevel"
                      value={center.licenseLevel}
                      onChange={handleInputChange}
                    >
                      <option value="">Select License Level</option>
                      <option value="essential">Essential</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="licenseStartDate">License Start Date</label>
                    <input
                      type="date"
                      id="licenseStartDate"
                      name="licenseStartDate"
                      value={center.licenseStartDate ? center.licenseStartDate.split('T')[0] : ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="licenseEndDate">License End Date</label>
                    <input
                      type="date"
                      id="licenseEndDate"
                      name="licenseEndDate"
                      value={center.licenseEndDate ? center.licenseEndDate.split('T')[0] : ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => navigate(centerId ? '/centers' : '/settings')}
              >
                Cancel
              </button>
              {centerId && (
                <button 
                  type="button" 
                  className="delete-button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={submitting}
                  style={{ 
                    display: 'block !important',
                    visibility: 'visible !important',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px'
                  }}
                >
                  Delete Center
                </button>
              )}
              <button 
                type="submit" 
                className="save-button"
                disabled={submitting}
              >
                {submitting ? (centerId ? 'Saving...' : 'Creating...') : (centerId ? 'Save Changes' : 'Create Center')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Center</h3>
            <p>Are you sure you want to delete this center? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="delete-button"
                onClick={handleDelete}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCenterPage;